import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { eur } from "@/lib/api";
import { Plus, Minus, Trash2, ChevronLeft } from "lucide-react";

// `selected` = lignes du panier existant ajoutées depuis ce pop-up (aucun second panier).
export default function UpsellSheet({ open, suggestions, selected = [], onClose, onAdd, onChangeQty, onRemove, t, theme = { primary: "#123026", gold: "#C69C4A" } }) {
  const [view, setView] = useState("list");
  useEffect(() => { if (open) setView("list"); }, [open]);
  useEffect(() => { if (view === "selection" && selected.length === 0) setView("list"); }, [selected.length, view]);
  const count = selected.reduce((s, i) => s + i.quantity, 0);
  const total = selected.reduce((s, i) => s + i.line_total, 0);
  const qtyOf = (pid) => selected.filter((i) => i.product_id === pid).reduce((s, i) => s + i.quantity, 0);
  const fr = (t.noThanks || "").startsWith("Non");
  const L = fr
    ? { view: "Voir ma sélection", back: "Retour aux suggestions", sel: (n) => `${n} supplément${n > 1 ? "s" : ""} sélectionné${n > 1 ? "s" : ""}`, go: "Ajouter et continuer", title: "Ma sélection" }
    : { view: "View my selection", back: "Back to suggestions", sel: (n) => `${n} extra${n > 1 ? "s" : ""} selected`, go: "Add and continue", title: "My selection" };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        data-testid="upsell-modal"
        className="p-0 gap-0 max-w-md w-[calc(100%-1.5rem)] rounded-3xl overflow-hidden border-none bg-[#FBF9F5]"
      >
        <DialogTitle className="sr-only">{t.upsellTitle}</DialogTitle>
        <DialogDescription className="sr-only">{t.upsellSubtitle}</DialogDescription>
        <div className="px-5 pt-6 pb-2 text-center">
          <h2 className="font-serif-display text-2xl text-[#123026]">{view === "list" ? t.upsellTitle : L.title}</h2>
          <p className="text-sm text-[#8A918D] mt-1">{view === "list" ? t.upsellSubtitle : L.sel(count)}</p>
        </div>

        {view === "list" ? (
          <div className="px-5 py-4 space-y-2.5 max-h-[46vh] overflow-y-auto no-scrollbar">
            {suggestions.map((p) => {
              const q = qtyOf(p.id);
              return (
                <div key={p.id} className="flex items-center gap-3 rounded-2xl bg-white border border-[#EAE5DC] p-2.5">
                  <img src={p.image} alt={p.name} className="w-14 h-14 rounded-xl object-cover shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-[#123026] truncate">{p.name}</p>
                    <p className="text-sm font-bold text-[#C69C4A]">+{eur(p.price)}</p>
                  </div>
                  {q > 0 && <span data-testid={`upsell-qty-${p.id}`} className="text-xs font-bold text-white rounded-full w-6 h-6 grid place-items-center" style={{ backgroundColor: theme.gold }}>{q}</span>}
                  <Button data-testid={`upsell-add-${p.id}`} onClick={() => onAdd(p)} size="sm" className="rounded-full text-white h-9 w-9 p-0" style={{ backgroundColor: theme.primary }}>
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="px-5 py-4 space-y-2.5 max-h-[46vh] overflow-y-auto no-scrollbar" data-testid="upsell-selection">
            {selected.map((i) => (
              <div key={i.cart_id} data-testid={`upsell-sel-${i.cart_id}`} className="flex items-center gap-3 rounded-2xl bg-white border border-[#EAE5DC] p-2.5">
                <img src={i.image} alt={i.name} className="w-12 h-12 rounded-xl object-cover shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-[#123026] truncate">{i.name}</p>
                  <p className="text-xs text-[#8A918D]">{eur(i.unit_price)} × {i.quantity} · <b className="text-[#C69C4A]">{eur(i.line_total)}</b></p>
                </div>
                <div className="flex items-center gap-1.5">
                  <button data-testid={`upsell-dec-${i.cart_id}`} onClick={() => (i.quantity > 1 ? onChangeQty(i.cart_id, -1) : onRemove(i.cart_id))} className="w-8 h-8 rounded-full bg-[#EBF2EE] text-[#123026] grid place-items-center"><Minus className="w-3.5 h-3.5" /></button>
                  <span data-testid={`upsell-sel-qty-${i.cart_id}`} className="w-5 text-center text-sm font-bold text-[#123026]">{i.quantity}</span>
                  <button data-testid={`upsell-inc-${i.cart_id}`} onClick={() => onChangeQty(i.cart_id, 1)} className="w-8 h-8 rounded-full text-white grid place-items-center" style={{ backgroundColor: theme.primary }}><Plus className="w-3.5 h-3.5" /></button>
                  <button data-testid={`upsell-remove-${i.cart_id}`} onClick={() => onRemove(i.cart_id)} className="w-8 h-8 rounded-full text-[#B4462F] hover:bg-[#F7E7E3] grid place-items-center ml-1"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="px-5 pb-5 pt-1 space-y-2">
          {count > 0 && (
            <div data-testid="upsell-summary" className="flex items-center justify-between rounded-2xl bg-white border border-[#EAE5DC] px-4 py-2.5 text-sm">
              <span className="text-[#5C615E] font-medium">{L.sel(count)}</span>
              <span data-testid="upsell-total" className="font-bold text-[#123026]">+{eur(total)}</span>
            </div>
          )}
          {count > 0 && view === "list" && (
            <Button data-testid="upsell-view-selection" onClick={() => setView("selection")} variant="outline" className="w-full h-11 rounded-full border-[#DDE5E0] text-[#123026] font-semibold bg-white">
              {L.view} ({count})
            </Button>
          )}
          {view === "selection" && (
            <Button data-testid="upsell-back" onClick={() => setView("list")} variant="ghost" className="w-full h-10 rounded-full text-[#5C615E] hover:bg-[#EBF2EE] font-semibold">
              <ChevronLeft className="w-4 h-4 mr-1" /> {L.back}
            </Button>
          )}
          {count > 0 ? (
            <Button data-testid="upsell-confirm" onClick={onClose} className="w-full h-11 rounded-full text-white font-semibold" style={{ backgroundColor: theme.primary }}>
              {L.go}
            </Button>
          ) : (
            <Button data-testid="upsell-skip" onClick={onClose} variant="ghost" className="w-full h-11 rounded-full text-[#5C615E] hover:bg-[#EBF2EE] font-semibold">
              {t.noThanks}
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
